// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

import { z } from 'zod'
import { idParam } from './id-validators'

export const DECLARATIE_TYPE = [
  'REISKOSTEN',
  'LUNCH',
  'DINER',
  'KLEDING',
  'GEREEDSCHAP',
  'HOTEL',
  'PARKEREN',
  'OVERIG',
] as const
export type DeclaratieType = (typeof DECLARATIE_TYPE)[number]

export const DECLARATIE_STATUS = [
  'CONCEPT',
  'INGEDIEND',
  'GOEDGEKEURD_TEAMLEIDER',
  'AFGEKEURD_TEAMLEIDER',
  'GOEDGEKEURD_HR',
  'AFGEKEURD_HR',
  'UITBETAALD',
  'INGETROKKEN',
  // FO-055 (batch-17) P2 — beoordelaar stelt een aanvullende vraag (zonder goed/af
  // te keuren); declaratie staat tijdelijk bij de medewerker, keert na antwoord
  // terug naar INGEDIEND. Spiegelt LeaveStatus.AANVULLENDE_VRAAG.
  'AANVULLENDE_VRAAG',
] as const
export type DeclaratieStatus = (typeof DECLARATIE_STATUS)[number]

export const DECLARATIE_AUDIT_ACTIE = [
  'INGEDIEND',
  'GOEDGEKEURD_TEAMLEIDER',
  'AFGEKEURD_TEAMLEIDER',
  'GOEDGEKEURD_HR',
  'AFGEKEURD_HR',
  'UITBETAALD',
  'INGETROKKEN',
  'WIJZIGING',
  'BIJLAGE_TOEGEVOEGD',
  'BIJLAGE_VERWIJDERD',
  // FO-336 (batch-39) — expliciete bon-download (per bon of alles-als-ZIP),
  // conform het bestaande download-audit-patroon (DocumentAudit GEDOWNLOAD).
  'BON_GEDOWNLOAD',
  // FO-405 (batch-50) — boekstuk doorgezet naar het Zenvoices-parse-adres van een
  // BV; herverzenden is een aparte, expliciete actie (met reden).
  'VERSTUURD_ZENVOICES',
  'HERVERZONDEN_ZENVOICES',
] as const
export type DeclaratieAuditActie = (typeof DECLARATIE_AUDIT_ACTIE)[number]

/**
 * FO-405 (batch-50) — de statussen waarin een declaratie naar de boekhouding mag.
 * Alleen ná eindgoedkeuring: een afgekeurde of nog lopende bon hoort nooit in
 * Zenvoices. UITBETAALD-zonder-stempel mag alsnog (inhaalslag).
 */
export const DECLARATIE_ZENVOICES_STATUSSEN = [
  'GOEDGEKEURD_HR',
  'UITBETAALD',
] as const satisfies ReadonlyArray<DeclaratieStatus>

/** Mag deze declaratie-status naar Zenvoices doorgezet worden? */
export function magNaarZenvoices(status: DeclaratieStatus): boolean {
  return (DECLARATIE_ZENVOICES_STATUSSEN as ReadonlyArray<DeclaratieStatus>).includes(status)
}

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const DECLARATIE_TYPE_LABELS: Record<DeclaratieType, string> = {
  REISKOSTEN:  'Reiskosten',
  LUNCH:       'Lunch',
  DINER:       'Diner',
  KLEDING:     'Kleding',
  GEREEDSCHAP: 'Gereedschap',
  HOTEL:       'Hotel',
  PARKEREN:    'Parkeren',
  OVERIG:      'Overig',
}

export const DECLARATIE_STATUS_LABELS: Record<DeclaratieStatus, string> = {
  CONCEPT:                'Concept',
  INGEDIEND:              'Ingediend',
  GOEDGEKEURD_TEAMLEIDER: 'Goedgekeurd door teamleider',
  AFGEKEURD_TEAMLEIDER:   'Afgekeurd door teamleider',
  GOEDGEKEURD_HR:         'Goedgekeurd door HR',
  AFGEKEURD_HR:           'Afgekeurd door HR',
  UITBETAALD:             'Uitbetaald',
  INGETROKKEN:            'Ingetrokken',
  AANVULLENDE_VRAAG:      'Aanvullende vraag',
}

export const DECLARATIE_AUDIT_ACTIE_LABELS: Record<DeclaratieAuditActie, string> = {
  INGEDIEND:              'Ingediend',
  GOEDGEKEURD_TEAMLEIDER: 'Goedgekeurd door teamleider',
  AFGEKEURD_TEAMLEIDER:   'Afgekeurd door teamleider',
  GOEDGEKEURD_HR:         'Goedgekeurd door HR',
  AFGEKEURD_HR:           'Afgekeurd door HR',
  UITBETAALD:             'Uitbetaald',
  INGETROKKEN:            'Ingetrokken',
  WIJZIGING:              'Wijziging',
  BIJLAGE_TOEGEVOEGD:     'Bijlage toegevoegd',
  BIJLAGE_VERWIJDERD:     'Bijlage verwijderd',
  BON_GEDOWNLOAD:         'Bon gedownload',
  VERSTUURD_ZENVOICES:    'Doorgezet naar Zenvoices',
  HERVERZONDEN_ZENVOICES: 'Opnieuw naar Zenvoices verstuurd',
}

// Status badge variant voor StatusBadge-component.
// CONCEPT=secondary, INGEDIEND=info, GOEDGEKEURD_TEAMLEIDER=info,
// AFGEKEURD_*=danger, GOEDGEKEURD_HR=warning (wachten op uitbetaling),
// UITBETAALD=success, INGETROKKEN=secondary
export const DECLARATIE_STATUS_BADGE_VARIANT: Record<
  DeclaratieStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT:                'secondary',
  INGEDIEND:              'info',
  GOEDGEKEURD_TEAMLEIDER: 'info',
  AFGEKEURD_TEAMLEIDER:   'danger',
  GOEDGEKEURD_HR:         'warning',
  AFGEKEURD_HR:           'danger',
  UITBETAALD:             'success',
  INGETROKKEN:            'secondary',
  // Wacht op actie van de medewerker (openstaande vraag) — warning, geen afwijzing.
  AANVULLENDE_VRAAG:      'warning',
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface DeclaratieListRowDTO {
  id: string
  employeeId: string
  employeeName: string         // computed: firstName + lastName
  employeeNumber: string
  type: DeclaratieType
  omschrijving: string         // truncated tot 80 chars in lijst
  bedrag: number               // numeric, cent-precisie
  datum: string                // ISO yyyy-MM-dd
  status: DeclaratieStatus
  attachmentsAantal: number    // count van actieve attachments
  ingediendOp: string | null   // ISO datetime

  // ── FO-405 (batch-50) — doorzetten naar Zenvoices (apart spoor) ────────────
  /** ISO datetime van de aanbieding aan de mailqueue; null = nog door te zetten. */
  zenvoicesVerzondenOp: string | null
  /** Naam van de verzender (of het userId als de naam niet meer resolvet). */
  zenvoicesVerzondenDoorName: string | null
  /** BV waaronder het boekstuk is aangeboden. */
  zenvoicesEntityId: string | null
  zenvoicesEntityNaam: string | null
  /**
   * Staat deze declaratie in een status die naar de boekhouding mag
   * (GOEDGEKEURD_HR/UITBETAALD)? Stuurt de bulk-selectie in de lijst.
   */
  zenvoicesVerzendbaar: boolean
}

/**
 * FO-405 (batch-50) — de Zenvoices-blok van het declaratie-detail: de stempel
 * (wie/wanneer/BV), het BV-voorstel voor de verzendknop en de gate-informatie
 * waarmee de UI de knop toont, uitschakelt of van uitleg voorziet.
 */
export interface DeclaratieZenvoicesDTO {
  /** Stempel: ISO datetime van de succesvolle aanbieding aan de mailqueue. */
  verzondenOp: string | null
  verzondenDoor: string | null
  verzondenDoorName: string | null
  /** De BV waaronder het boekstuk daadwerkelijk is aangeboden. */
  entityId: string | null
  entityNaam: string | null

  /** Status-gate: GOEDGEKEURD_HR of UITBETAALD. */
  statusToegestaan: boolean
  /** Voorstel-BV: het dienstverband van de medewerker op de declaratiedatum. */
  voorstelEntityId: string | null
  voorstelEntityNaam: string | null
  /**
   * Heeft de voorstel-BV een Zenvoices-parse-adres? Zo niet: knop disabled met
   * uitleg + link naar /instellingen/entiteiten (geen stille no-op).
   */
  voorstelHeeftParseAdres: boolean
  /**
   * Viel de declaratiedatum buiten het dienstverbandvenster van de medewerker?
   * Dan is het voorstel een aanname en maakt de picker de keuze expliciet.
   */
  voorstelBuitenDienstverband: boolean
}

export interface DeclaratieDetailDTO {
  id: string
  employeeId: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string
    functionTitle: string | null
  }
  type: DeclaratieType
  omschrijving: string
  bedrag: number
  datum: string                // ISO yyyy-MM-dd
  status: DeclaratieStatus
  gekoppeldAan: string | null

  // Stap 1: TL review
  teamleiderReviewedBy: string | null
  goedgekeurdTeamleiderDoorName: string | null  // UI-naam voor reviewer name
  goedgekeurdTeamleiderOp: string | null  // ISO datetime — UI-naam voor TL-review timestamp
  teamleiderRejectionReason: string | null

  // Stap 2: HR review
  hrReviewedBy: string | null
  goedgekeurdHrDoorName: string | null  // UI-naam voor reviewer name
  goedgekeurdHrOp: string | null  // ISO datetime — UI-naam voor HR-review timestamp
  hrRejectionReason: string | null

  // Stap 3: uitbetaling
  uitbetaaldOp: string | null
  uitbetaaldDoor: string | null
  uitbetaaldDoorName: string | null

  /**
   * Eerste afwijzings-reden (TL of HR) als de declaratie is afgekeurd.
   * UI-driven veld om de afgewezen-banner te kunnen tonen zonder per-stap
   * te kiezen. Komt uit teamleiderRejectionReason ?? hrRejectionReason.
   */
  afwijzingReden: string | null

  ingediendOp: string | null
  attachments: DeclaratieAttachmentDTO[]
  audits: DeclaratieAuditDTO[]
  createdAt: string
  updatedAt: string

  /** FO-405 (batch-50) — doorzetten naar Zenvoices (stempel + BV-voorstel). */
  zenvoices: DeclaratieZenvoicesDTO
}

export interface DeclaratieAttachmentDTO {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
  downloadUrl: string          // computed `/api/v1/declaraties/:id/attachments/:attachmentId`
  /**
   * FO-336 — server-side berekende FO-152-downloadnaam:
   * `Declaratiebon_[medewerker]_[declaratiedatum]_[volgnr].[ext]`. De download-
   * knop (`?download=1`) levert het origineel onder exact deze naam; de UI
   * toont/gebruikt dezelfde naam zodat client en server nooit verschillen.
   */
  downloadFileName: string
  createdAt: string
}

export interface DeclaratieAuditDTO {
  id: string
  actie: DeclaratieAuditActie
  actor: string
  actorName: string | null
  oudeStatus: DeclaratieStatus | null
  nieuweStatus: DeclaratieStatus | null
  /**
   * UI-driven uitbreiding: free-form change-payload als JSON-blob. Backend
   * mapt huidige status-transitions naar `{ status: <enum> }`.
   */
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

export interface DeclaratieStatsDTO {
  periode: { start: string; end: string }       // ISO dates
  totalIngediend: number                        // bedrag-totaal van INGEDIEND-records in periode
  totalGoedgekeurd: number                      // bedrag-totaal van GOEDGEKEURD_HR-records
  totalUitbetaald: number                       // bedrag-totaal van UITBETAALD-records
  byType: Record<string, number>                // som per categorie
  /** UI-driven KPI's voor backoffice dashboard. Aantallen per workflow-stap. */
  conceptAantal: number                         // CONCEPT (nog niet ingediend)
  ingediendAantal: number                       // INGEDIEND + GOEDGEKEURD_TEAMLEIDER (alle in pipeline)
  tlReviewAantal: number                        // INGEDIEND (wacht op TL)
  hrReviewAantal: number                        // GOEDGEKEURD_TEAMLEIDER (wacht op HR)
  uitbetaaldDezeMaand: {
    totaal: number                              // bedrag-totaal uitbetaald in huidige kalendermaand
    aantal: number                              // aantal records uitbetaald in huidige kalendermaand
  }
  totaalOpenstaand: number                      // bedrag-totaal van alle nog-niet-uitbetaalde records (CONCEPT/INGEDIEND/GOEDGEKEURD_*)
}

// ─── Zod-schemas voor request bodies ─────────────────────────────────────────

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat (yyyy-MM-dd)')

export const createDeclaratieSchema = z.object({
  type:         z.enum(DECLARATIE_TYPE),
  omschrijving: z.string().trim().min(1).max(500),
  bedrag:       z.number().positive('Bedrag moet positief zijn'),
  datum:        isoDate,
  gekoppeldAan: z.string().trim().max(200).optional(),
  // Backoffice (TL/HR/Admin) mag namens een andere medewerker indienen door
  // diens employeeId mee te sturen. Portal-users (monteurs) negeren dit veld
  // server-side en boeken altijd voor zichzelf - zie POST /declaraties.
  employeeId:   idParam('employeeId').optional(),
})

export const updateDeclaratieSchema = z.object({
  type:         z.enum(DECLARATIE_TYPE).optional(),
  omschrijving: z.string().trim().min(1).max(500).optional(),
  bedrag:       z.number().positive().optional(),
  datum:        isoDate.optional(),
  gekoppeldAan: z.string().trim().max(200).nullable().optional(),
})

export const submitDeclaratieSchema = z.object({}).strict()

export const withdrawDeclaratieSchema = z.object({
  note: z.string().trim().max(500).optional(),
})

export const reviewTeamleiderSchema = z.object({
  decision: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
  note:     z.string().trim().max(500).optional(),
})

export const reviewHrSchema = z.object({
  decision: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
  note:     z.string().trim().max(500).optional(),
})

// FO-055 (batch-17) P2 — aanvullende vraag op een declaratie. Eén gedeeld schema:
// de API valideert ermee EN het form valideert er client-side mee, zodat het form
// nooit iets stuurt dat de API weigert (CLAUDE.md "Validatie"). Spiegelt het
// verlof-equivalent: vraag min. 10 tekens, antwoord verplicht.
export const aanvullendeVraagDeclaratieSchema = z.object({
  vraag: z
    .string()
    .trim()
    .min(10, 'Vraag moet minimaal 10 tekens bevatten')
    .max(1000, 'Vraag mag maximaal 1000 tekens bevatten'),
})
export type AanvullendeVraagDeclaratieInput = z.infer<
  typeof aanvullendeVraagDeclaratieSchema
>

export const beantwoordVraagDeclaratieSchema = z.object({
  antwoord: z
    .string()
    .trim()
    .min(1, 'Antwoord is verplicht')
    .max(1000, 'Antwoord mag maximaal 1000 tekens bevatten'),
})
export type BeantwoordVraagDeclaratieInput = z.infer<
  typeof beantwoordVraagDeclaratieSchema
>

export const markUitbetaaldSchema = z.object({
  uitbetaaldOp: isoDate,
})

// ─── FO-405 (batch-50) — doorzetten naar Zenvoices ───────────────────────────
//
// Eén gedeeld schema: de API valideert ermee én het verzend-dialoog bouwt er
// zijn payload mee (CLAUDE.md "Validatie"), zodat het form nooit iets stuurt dat
// de API weigert.

/** Filterwaarde voor de lijst-/exportkolom "doorzetten naar Zenvoices". */
export const DECLARATIE_ZENVOICES_FILTER = ['open', 'verstuurd'] as const
export type DeclaratieZenvoicesFilter = (typeof DECLARATIE_ZENVOICES_FILTER)[number]

export const verzendDeclaratieZenvoicesSchema = z.object({
  /**
   * BV-override: leeg = het voorstel (dienstverband op declaratiedatum). Komt uit
   * de `EntityPicker`, nooit uit een vrij tekstveld.
   */
  entityId: idParam('entityId').optional(),
  /** Bewuste herverzending naar de boekhouding (stempel wordt bijgewerkt). */
  opnieuw: z.boolean().optional(),
  /** Bij `opnieuw` verplicht: waarom gaat het boekstuk nogmaals de deur uit. */
  reden: z.string().trim().max(500).optional(),
  /**
   * Declaratie zonder bonbijlage: versturen kan (het voorblad is dan het
   * boekstuk), maar alleen na een expliciete bevestiging.
   */
  bevestigZonderBon: z.boolean().optional(),
}).superRefine((val, ctx) => {
  if (val.opnieuw === true && (val.reden ?? '').trim().length < 3) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reden'],
      message: 'Geef een reden op voor het opnieuw versturen (minimaal 3 tekens)',
    })
  }
})
export type VerzendDeclaratieZenvoicesInput = z.infer<typeof verzendDeclaratieZenvoicesSchema>

/**
 * Bulk "Alle goedgekeurde doorzetten". De client stuurt de ids van de GEFILTERDE
 * lijst mee (max 200 per aanroep; de UI hakt een grotere selectie in blokken en
 * telt de samenvattingen op - nooit stil afkappen).
 */
export const BULK_ZENVOICES_MAX = 200

/**
 * Blokgrootte waarin de client een grotere selectie opknipt. Bewust ruim onder
 * `BULK_ZENVOICES_MAX`: elk boekstuk kost een PDF-render (pdf-lib + sharp) plus
 * een queue-insert, dus 200 in één request zou tegen de ingress-timeout aanlopen
 * en de API-instance CPU-bound maken. Kleine blokken houden elke aanroep kort en
 * bewaren de al behaalde resultaten als er onderweg iets misgaat.
 *
 * 10 en niet hoger vanwege de weekstaten-les: een CPU-bound endpoint dat lang
 * bezig blijft, bezet de instance en laat andere requests wachten. Elk boekstuk
 * doet een blob-read per bon + een `sharp`-resize + een pdf-lib-save; bij zware
 * bonfoto's telt dat snel op.
 */
export const BULK_ZENVOICES_BLOK = 10

export const bulkVerzendDeclaratiesZenvoicesSchema = z.object({
  ids: z.array(idParam('id')).min(1).max(BULK_ZENVOICES_MAX),
  bevestigZonderBon: z.boolean().optional(),
})
export type BulkVerzendDeclaratiesZenvoicesInput = z.infer<
  typeof bulkVerzendDeclaratiesZenvoicesSchema
>

/**
 * Uitkomst per declaratie. `VERSTUURD`/`HERVERZONDEN` tellen als verstuurd,
 * `AL_VERSTUURD`/`STATUS_NIET_TOEGESTAAN`/`GEEN_PARSE_ADRES`/`GEEN_BV`/
 * `BEVESTIGING_VEREIST` als overgeslagen, de rest als mislukt.
 */
export const DECLARATIE_ZENVOICES_UITKOMST = [
  'VERSTUURD',
  'HERVERZONDEN',
  'AL_VERSTUURD',
  'STATUS_NIET_TOEGESTAAN',
  'GEEN_BV',
  'GEEN_PARSE_ADRES',
  'BEVESTIGING_VEREIST',
  'NIET_GEVONDEN',
  'PDF_MISLUKT',
  'TE_GROOT',
  'MAIL_GEBLOKKEERD',
  'FOUT',
] as const
export type DeclaratieZenvoicesUitkomst = (typeof DECLARATIE_ZENVOICES_UITKOMST)[number]

export interface DeclaratieZenvoicesResultaatDTO {
  id: string
  uitkomst: DeclaratieZenvoicesUitkomst
  /** Medewerkernaam voor de samenvatting/het foutrapport. */
  medewerker: string | null
  /** BV waaronder verstuurd (of waaronder het geprobeerd is). */
  entityNaam: string | null
  /** Mensleesbare reden bij overslaan/mislukken. */
  reden: string | null
  /** Zenvoices-adres waarheen het boekstuk ging (alleen bij succes). */
  naarAdres: string | null
}

/**
 * BV-keuze voor het verzend-dialoog. Draait op DECLARATIES-rechten (niet op
 * ENTITEITEN.canView, dat een salarisadmin niet heeft) en bevat bewust NIET het
 * parse-adres zelf - alleen of het is ingesteld.
 */
export interface DeclaratieZenvoicesBvDTO {
  id: string
  naam: string
  heeftParseAdres: boolean
}

export interface DeclaratieZenvoicesBulkDTO {
  samenvatting: { verstuurd: number; overgeslagen: number; mislukt: number }
  /** Per BV geteld - de bulk groepeert de verzending per BV. */
  perBv: Array<{ entityNaam: string; verstuurd: number }>
  resultaten: DeclaratieZenvoicesResultaatDTO[]
}

// ─── FO-336 (batch-39) — accountant-export (bundel met overzicht + bonnen) ────
//
// Eén gedeeld schema: de export-definitie (`declaraties-accountant`) valideert
// ermee én de backoffice-modal bouwt er zijn payload mee (CLAUDE.md "Validatie").
// De filters spiegelen de actieve lijstfilters; `status` leeg = de accountant-
// default GOEDGEKEURD_HR + UITBETAALD (server-side toegepast, één bron).
export const declaratiesAccountantFilterSchema = z.object({
  van:        isoDate.optional(),
  totEnMet:   isoDate.optional(),
  status:     z.enum(DECLARATIE_STATUS).optional(),
  type:       z.enum(DECLARATIE_TYPE).optional(),
  employeeId: idParam('employeeId').optional(),
  /** BV-filter (Employee.entityId). */
  bvId:       idParam('bvId').optional(),
  search:     z.string().trim().max(100).optional(),
  /**
   * FO-405 (batch-50) — hetzelfde Zenvoices-filter als de lijst, zodat "wat je
   * ziet is wat je exporteert" ook geldt wanneer de lijst op "nog door te
   * zetten" staat.
   */
  zenvoices:  z.enum(DECLARATIE_ZENVOICES_FILTER).optional(),
  /** Bonbestanden in mappen per medewerker (default) of plat met FO-152-namen. */
  structuur:  z.enum(['mappen', 'plat']).default('mappen'),
})
export type DeclaratiesAccountantFilterInput = z.input<typeof declaratiesAccountantFilterSchema>
export type DeclaratiesAccountantFilters = z.output<typeof declaratiesAccountantFilterSchema>

/** Eén preview-rij = één declaratie uit dezelfde query als de bundel (FO-303-P3). */
export interface DeclaratiesAccountantPreviewRijDTO {
  id: string
  medewerker: string
  employeeNumber: string
  datum: string                // ISO yyyy-MM-dd
  bedrag: number
  status: DeclaratieStatus
  /** Aantal bonbestanden bij deze declaratie; 0 = "geen bon"-markering. */
  bonnen: number
}

export interface DeclaratiesAccountantPreviewDTO {
  aantalDeclaraties: number
  aantalBonnen: number
  /** Declaraties zonder enige bon — gemarkeerd, blokkeert de export niet. */
  aantalZonderBon: number
  aantalMedewerkers: number
  /** Decimal-som van de bedragen, afgerond op 2 decimalen. */
  totaalBedrag: number
  /** De statusfilter die de bundel daadwerkelijk gebruikt (expliciet of default). */
  statusFilterToegepast: DeclaratieStatus[]
  periode: { van: string | null; totEnMet: string | null }
  /** FO-152-naam die de ZIP bij bevestigen krijgt (incl. aanmaakdatum). */
  zipNaam: string
  /** Preview-rijen (gecapped op `previewRijenMax`; totalen dekken altijd alles). */
  rijen: DeclaratiesAccountantPreviewRijDTO[]
  rijenAfgekapt: boolean
}

export const listDeclaratiesQuerySchema = z.object({
  status:     z.enum(DECLARATIE_STATUS).optional(),
  type:       z.enum(DECLARATIE_TYPE).optional(),
  employeeId: idParam().optional(),
  // scope=mine dwingt server-side filtering op de eigen Employee af, ongeacht
  // backoffice-access. De portal stuurt dit altijd mee; zonder scope toont de
  // backoffice-beheerlijst iedereen.
  scope:      z.enum(['mine']).optional(),
  vanaf:      isoDate.optional(),
  totEnMet:   isoDate.optional(),
  search:     z.string().trim().max(100).optional(),
  // FO-405 (batch-50) — server-side filter op het Zenvoices-spoor:
  // 'open' = eindgoedgekeurd én nog geen stempel ("nog door te zetten"),
  // 'verstuurd' = stempel aanwezig.
  zenvoices:  z.enum(DECLARATIE_ZENVOICES_FILTER).optional(),
  page:       z.coerce.number().int().positive().default(1),
  // pageSize tot 2000: het backoffice-overzicht haalt de volledige (gefilterde)
  // set in één keer op en sorteert/pagineert client-side (zo werkt de kolom-
  // sortering globaal i.p.v. per server-pagina). Default blijft 20.
  pageSize:   z.coerce.number().int().positive().max(2000).default(20),
})
